import React, { useState, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { QK } from '../../lib/queryKeys';
import { useAuth } from '../../context/AuthContext';
import SidebarLayout from '../../components/layout/SidebarLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { ArrowLeft, Search, Loader2, ChevronDown, ChevronRight, Heart, FileText } from 'lucide-react';
import api from '../../services/api';

const ViewerSummaryPage = () => {
  const navigate = useNavigate();
  const { saleId } = useParams();
  const [searchParams] = useSearchParams();
  const saleName = searchParams.get('name') || '';
  const [search, setSearch] = useState('');

  const { data, isLoading: loading } = useQuery({
    queryKey: QK.viewerSummaryData(saleId),
    queryFn: async () => {
      const [summaryRes, saleRes] = await Promise.all([
        api.get(`/sale-cards/viewer-summary?sale_id=${saleId}`),
        api.get(`/sales/${saleId}`),
      ]);
      return { summary: summaryRes.data || [], saleName: saleRes.data?.sale_name || saleName };
    },
    enabled: !!saleId,
  });
  const summary          = data?.summary         ?? [];
  const saleNameResolved = data?.saleName         ?? saleName;

  // Sort alphabetically by last name (last word) for persons, or full name for orgs
  const sortKey = (name = '') => {
    const parts = name.trim().split(/\s+/);
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : name.toLowerCase();
  };

  const filtered = useMemo(() => summary
    .filter(v => !search || v.viewer_name?.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => sortKey(a.viewer_name).localeCompare(sortKey(b.viewer_name))),
  [summary, search]); // eslint-disable-line react-hooks/exhaustive-deps

  const { user: currentUser } = useAuth();
  const isCardRunner = currentUser?.roles?.includes('tenant_card_runner') && !currentUser?.roles?.some(r => ['tenant_system_admin', 'tenant_acct_manager', 'tenant_team_leader'].includes(r));

  if (isCardRunner) {
    return (
      <SidebarLayout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">You do not have access to the Viewer Summary.</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate('/sales?tab=live')}>Go Back</Button>
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/sales?tab=live')}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <div>
              <h1 className="font-serif text-2xl font-semibold">Viewer Summary</h1>
              <p className="text-sm text-muted-foreground">{saleNameResolved}</p>
            </div>
          </div>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">All Viewers ({filtered.length})</CardTitle>
              <div className="relative max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input placeholder="Search viewers..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-8 text-sm" />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : filtered.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No viewers recorded yet.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8"></TableHead>
                    <TableHead>Viewer</TableHead>
                    <TableHead>Industry Role</TableHead>
                    <TableHead>Cards</TableHead>
                    <TableHead>Total Viewings</TableHead>
                    <TableHead>Vetting Procedures</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((v, i) => (
                    <ViewerRow key={i} viewer={v} saleId={saleId} navigate={navigate} />
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </SidebarLayout>
  );
};

/* --- Expandable Viewer Row --- */
const ViewerRow = ({ viewer, saleId, navigate }) => {
  const [expanded, setExpanded] = useState(false);
  const [cardGroups, setCardGroups] = useState([]);
  const [vettingProcedures, setVettingProcedures] = useState([]);
  const [viewerEntity, setViewerEntity] = useState(null);
  const [associations, setAssociations] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const loadViewings = async () => {
    if (loaded) { setExpanded(!expanded); return; }
    try {
      const [cardsRes, vettingRes] = await Promise.all([
        api.get(`/sale-cards?sale_id=${saleId}`),
        api.get(`/vetting-procedures?sale_id=${saleId}`),
      ]);
      const viewerCards = (cardsRes.data || []).filter(c =>
        c.viewer_crm_entity_id === viewer.viewer_crm_entity_id ||
        (!c.viewer_crm_entity_id && c.viewer_name === viewer.viewer_name)
      );
      // Filter vetting procedures for this viewer (match by VET, not client)
      const viewerVetting = (vettingRes.data || []).filter(vp =>
        vp.vet_crm_entity_id === viewer.viewer_crm_entity_id ||
        (!viewer.viewer_crm_entity_id && vp.vet_name === viewer.viewer_name)
      );
      setVettingProcedures(viewerVetting);
      const groups = [];
      for (const card of viewerCards) {
        try {
          const cardRes = await api.get(`/sale-cards/${card.id}`);
          groups.push({ card, viewings: cardRes.data?.viewings || [] });
          if (cardRes.data?.viewer_entity && !viewerEntity) setViewerEntity(cardRes.data.viewer_entity);
          if (cardRes.data?.viewer_associations?.length) setAssociations(cardRes.data.viewer_associations);
        } catch { groups.push({ card, viewings: [] }); }
      }
      setCardGroups(groups);
      setLoaded(true);
    } catch { /* ignore */ }
    setExpanded(true);
  };

  return (
    <>
      <TableRow className="cursor-pointer hover:bg-muted/30" onClick={loadViewings}>
        <TableCell className="w-8 p-2">
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </TableCell>
        <TableCell className="font-medium">
          <span
            className="text-primary hover:underline cursor-pointer"
            onClick={(e) => { e.stopPropagation(); if (viewer.viewer_crm_entity_id) navigate(`/crm/accounts/${viewer.viewer_crm_entity_id}`); }}
          >
            {viewer.viewer_name}
          </span>
        </TableCell>
        <TableCell className="text-xs text-muted-foreground capitalize">{viewer.viewer_role?.replace(/_/g, ' ') || '-'}</TableCell>
        <TableCell>{viewer.total_cards}</TableCell>
        <TableCell>{viewer.completed_viewings} of {viewer.total_viewings} requested</TableCell>
        <TableCell>{viewer.vetting_procedures || 0}</TableCell>
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={7} className="p-0">
            <div className="bg-muted/20 px-8 py-3 space-y-3">
              {/* Sale Preferences & Key Associations */}
              <div className="grid grid-cols-2 gap-4 text-xs">
                <div>
                  <span className="font-semibold">Sale Preferences: </span>
                  <span className="text-muted-foreground">{viewerEntity?.sales_preferences || 'None noted'}</span>
                  {viewerEntity?.willing_to_buy_off_sheet && (
                    <Badge className="ml-2 bg-primary text-white text-xs py-0">Willing to buy off-sheet</Badge>
                  )}
                </div>
                <div>
                  <span className="font-semibold">Key Associations: </span>
                  {associations.length === 0 ? (
                    <span className="text-muted-foreground">None noted</span>
                  ) : (
                    <span>{associations.map(a => `${a.other_entity_name} (${a.relationship?.replace(/_/g, ' ')})`).join(', ')}</span>
                  )}
                </div>
              </div>

              {/* Viewings grouped by card */}
              {cardGroups.length === 0 ? (
                <p className="text-xs text-muted-foreground">No viewings found</p>
              ) : (
                cardGroups.map(({ card, viewings }) => (
                  <div key={card.id} className="border rounded p-3 bg-white">
                    <div className="flex items-center gap-2 mb-2 text-xs text-muted-foreground">
                      <span className="font-semibold text-foreground">Card</span>
                      <span>{card.date ? new Date(card.date).toLocaleDateString() : '-'}</span>
                      {card.card_type !== 'individual' && (
                        <Badge variant="outline" className="text-xs py-0 capitalize">{card.card_type.replace(/_/g, ' ')}</Badge>
                      )}
                      <Badge className={card.status === 'complete' ? 'bg-green-100 text-green-700 text-xs py-0' : 'bg-gray-100 text-gray-700 text-xs py-0'}>
                        {card.status?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                      </Badge>
                    </div>
                    <table className="w-full text-xs table-fixed">
                      <thead>
                        <tr className="text-muted-foreground">
                          <th className="text-left py-1 font-medium w-[10%]">Hip #</th>
                          <th className="text-left py-1 font-medium w-[18%]">Horse</th>
                          <th className="text-left py-1 font-medium w-[32%]">Status</th>
                          <th className="text-left py-1 font-medium w-[40%]">Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {viewings.map(v => (
                          <tr key={v.id} className="border-t border-border/50">
                            <td className="py-1.5 font-medium">#{v.hip_number || 'TBD'}</td>
                            <td className="py-1.5">{v.horse_name || '-'}</td>
                            <td className="py-1.5">
                              {v.status === 'completed' ? (
                                <span>
                                  <Badge className="bg-green-100 text-green-700 text-xs py-0">Done</Badge>
                                  {v.updated_at && <span className="text-muted-foreground ml-1">({new Date(v.updated_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })})</span>}
                                </span>
                              ) : v.status === 'cancelled' ? (
                                <Badge className="bg-red-100 text-red-700 text-xs py-0">Cancelled</Badge>
                              ) : (
                                <Badge className="bg-gray-100 text-gray-700 text-xs py-0">Pending</Badge>
                              )}
                            </td>
                            <td className="py-1.5">
                              {v.liked && <span title="Liked"><Heart className="w-3 h-3 text-red-500 fill-current inline mr-1" /></span>}
                              {v.viewed_vet_report && <span title="Viewed Vet Report"><FileText className="w-3 h-3 text-blue-500 inline mr-1" /></span>}
                              {v.notes && <span className="text-muted-foreground">{v.notes}</span>}
                              {!v.liked && !v.viewed_vet_report && !v.notes && <span className="text-muted-foreground">-</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))
              )}

              {/* Vetting Procedures */}
              {vettingProcedures.length > 0 && (
                <div className="border rounded p-3 bg-white">
                  <div className="flex items-center gap-2 mb-2 text-xs">
                    <span className="font-semibold text-foreground">Vetting Procedures</span>
                    <Badge variant="outline" className="text-xs py-0">{vettingProcedures.length}</Badge>
                  </div>
                  <table className="w-full text-xs table-fixed">
                    <thead>
                      <tr className="text-muted-foreground">
                        <th className="text-left py-1 font-medium w-[10%]">Hip #</th>
                        <th className="text-left py-1 font-medium w-[20%]">Horse</th>
                        <th className="text-left py-1 font-medium w-[20%]">Client</th>
                        <th className="text-left py-1 font-medium w-[20%]">Procedure</th>
                        <th className="text-left py-1 font-medium w-[15%]">Date</th>
                        <th className="text-left py-1 font-medium w-[15%]">Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vettingProcedures.map(vp => (
                        <tr key={vp.id} className="border-t border-border/50">
                          <td className="py-1.5 font-medium">#{vp.hip_number || 'TBD'}</td>
                          <td className="py-1.5">{vp.horse_name || '-'}</td>
                          <td className="py-1.5">{vp.client_name || '-'}</td>
                          <td className="py-1.5 capitalize">{vp.procedure?.replace(/_/g, ' ') || '-'}
                            {vp.viewed_vet_report && <FileText className="w-3 h-3 text-blue-500 inline ml-1" />}
                          </td>
                          <td className="py-1.5 text-muted-foreground">{vp.procedure_date_time ? new Date(vp.procedure_date_time).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '-'}</td>
                          <td className="py-1.5 text-muted-foreground">{vp.notes || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

export default ViewerSummaryPage;
